import axios from "axios";
import { getApiBaseUrl, getHeaders } from "./apiClient";
import { BackendApiResponse } from "./adminService";

// Types

export type ScrapeJobStatus = "pending" | "processing" | "completed" | "failed" | "partial";
export type SourceType = "website" | "facebook" | "instagram" | "tiktok";
export type ScrapeJobOrigin = "manual" | "fb_review_agent" | "discovery_agent" | "admin_rescrape";

export type ToolCallLog = {
  toolName: string;
  input: Record<string, unknown>;
  output?: Record<string, unknown>;
  error?: string;
  startedAt: string;
  completedAt?: string;
  durationMs?: number;
  skipped?: boolean;
  skipReason?: string;
  tokenUsage?: {
    inputTokens: number;
    outputTokens: number;
    costUsd: number;
  };
  costData?: {
    service: string;
    costUsd: number;
    units?: number;
    unitLabel?: string;
    metadata?: {
      cached?: boolean;
      originalCostUsd?: number;
      [key: string]: unknown;
    };
  };
  promptRef?: {
    promptId?: string;
    type: string;
    version: number;
  };
};

export type ScrapedSource = {
  platform: SourceType;
  url: string;
  extractedData: Record<string, unknown>;
};

export type ScrapeJob = {
  _id: string;
  targetUrl: string;
  status: ScrapeJobStatus;
  sourceType?: SourceType;
  starred?: boolean;
  origin?: ScrapeJobOrigin;
  originContext?: {
    note?: string;
    postReviewId?: string;
    fbPostId?: string;
    storeName?: string;
    searchQuery?: string;
    discoveryItemId?: string;
    discoveryCampaignId?: string;
    discoveryQuery?: string;
  };
  config: {
    maxPages: number;
  };
  extractedData?: Record<string, unknown>;
  toolCalls?: ToolCallLog[];
  scrapedSources?: ScrapedSource[];
  draftStoreId?: string;
  existingStoreId?: string;
  error?: string;
  attempts?: Array<{
    attemptNumber: number;
    startedAt: string;
    completedAt?: string;
    pagesProcessed: number;
    error?: string;
  }>;
  createdAt: string;
  updatedAt: string;
};

export type AgentModelOption = {
  id: string;
  label: string;
  pricing: { input: number; output: number };
};

// API functions

export const getAvailableModels = async (): Promise<BackendApiResponse<AgentModelOption[]>> => {
  try {
    const response = await axios.get(
      `${getApiBaseUrl()}/ecommerce-scrape/models`,
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to fetch available models" };
  }
};

// Identity of the store behind a dedup (409) rejection, so the UI can link to it
// instead of leaving the user at a dead-end "already exists" message.
export type StoreAlreadyExistsInfo = {
  storeId: string;
  slug?: string;
  name?: string;
};

export type StartScrapeJobResponse = BackendApiResponse<ScrapeJob> & {
  code?: string;
  existingStore?: StoreAlreadyExistsInfo;
};

export const startScrapeJob = async ({
  url,
  config,
  existingStoreId,
}: {
  url: string;
  config?: { maxPages?: number };
  existingStoreId?: string;
}): Promise<StartScrapeJobResponse> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}/ecommerce-scrape`,
      { url, config, existingStoreId },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return {
        ok: false,
        error: error.response.data.error,
        code: error.response.data.code,
        existingStore: error.response.data.existingStore,
      };
    }
    return { ok: false, error: "Failed to start scrape job" };
  }
};

export const listScrapeJobs = async ({
  page = 1,
  limit = 20,
  status,
  origin,
}: {
  page?: number;
  limit?: number;
  status?: ScrapeJobStatus;
  origin?: string;
} = {}): Promise<
  BackendApiResponse<ScrapeJob[]> & {
    pagination?: { current_page: number; total_pages: number; total_items: number };
  }
> => {
  try {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    if (status) params.append("status", status);
    if (origin) params.append("origin", origin);
    const response = await axios.get(
      `${getApiBaseUrl()}/ecommerce-scrape?${params}`,
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to fetch scrape jobs" };
  }
};

export const getScrapeJob = async (id: string): Promise<BackendApiResponse<ScrapeJob>> => {
  try {
    const response = await axios.get(
      `${getApiBaseUrl()}/ecommerce-scrape/${id}`,
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to fetch scrape job" };
  }
};

export const retryScrapeJob = async (id: string): Promise<BackendApiResponse<ScrapeJob>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}/ecommerce-scrape/${id}/retry`,
      {},
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to retry scrape job" };
  }
};

export const forceCreateStoreFromJob = async (
  id: string,
): Promise<BackendApiResponse<{ storeId: string; slug: string; merged?: boolean }>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}/ecommerce-scrape/${id}/force-create`,
      {},
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "Failed to force-create store" };
  }
};

export const cancelScrapeJob = async (id: string): Promise<BackendApiResponse<void>> => {
  try {
    const response = await axios.delete(
      `${getApiBaseUrl()}/ecommerce-scrape/${id}`,
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to cancel scrape job" };
  }
};

export const toggleStarScrapeJob = async (id: string): Promise<BackendApiResponse<{ starred: boolean }>> => {
  try {
    const response = await axios.patch(
      `${getApiBaseUrl()}/ecommerce-scrape/${id}/star`,
      {},
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to toggle star" };
  }
};
